import { useEffect, useRef, useState } from 'react';

interface TocEntry {
  id: string;
  text: string;
  level: number;
}

function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(
    () => globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false,
  );

  useEffect(() => {
    const mq = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return prefersReduced;
}

export function TableOfContents() {
  const [entries, setEntries] = useState<TocEntry[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const observerRef = useRef<IntersectionObserver | null>(null);
  const prefersReduced = usePrefersReducedMotion();

  useEffect(() => {
    const headings = document.querySelectorAll<HTMLHeadingElement>(
      'article h2[id], article h3[id]',
    );
    const tocEntries: TocEntry[] = Array.from(headings).map((h) => ({
      id: h.id,
      text: h.textContent ?? '',
      level: h.tagName === 'H2' ? 2 : 3,
    }));
    setEntries(tocEntries);

    if (tocEntries.length === 0) return;

    // IntersectionObserver is absent on some older WebViews and in jsdom
    // without a polyfill/mock. Skip live-highlight rather than crashing —
    // the TOC list itself still renders from the DOM heading scan above.
    if (typeof IntersectionObserver === 'undefined') return;

    observerRef.current?.disconnect();

    const visibleIds = new Set<string>();

    const observer = new IntersectionObserver(
      (intersectionEntries) => {
        for (const entry of intersectionEntries) {
          if (entry.isIntersecting) {
            visibleIds.add(entry.target.id);
          } else {
            visibleIds.delete(entry.target.id);
          }
        }

        // Pick the first visible heading in document order
        for (const tocEntry of tocEntries) {
          if (visibleIds.has(tocEntry.id)) {
            setActiveId(tocEntry.id);
            return;
          }
        }
      },
      { rootMargin: '-64px 0px -60% 0px', threshold: 0 },
    );

    observerRef.current = observer;

    for (const heading of headings) {
      observer.observe(heading);
    }

    return () => observer.disconnect();
  }, []);

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth' });
      // Update URL hash without scrolling
      history.replaceState(null, '', `#${id}`);
    }
  }

  if (entries.length === 0) return null;

  return (
    <aside role="complementary" aria-label="Table of contents">
      <nav className="sticky top-24">
        <ul className="flex flex-col gap-1 border-l border-[rgba(255,255,255,0.08)]">
          {entries.map((entry) => (
            <li key={entry.id}>
              <a
                href={`#${entry.id}`}
                onClick={(e) => handleClick(e, entry.id)}
                className={`block py-1 text-small transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 ${
                  entry.level === 3 ? 'pl-6' : 'pl-3'
                } ${
                  activeId === entry.id
                    ? 'text-primary border-l-2 border-cyan-400 -ml-px font-medium'
                    : 'text-secondary hover:text-primary'
                }`}
              >
                {entry.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}
